import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Moon, Sun, Globe, Menu, X, ArrowRight, Sparkles } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSiteI18n } from './SiteI18n';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const SiteHeader: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useSiteI18n();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => setOpen(false), [pathname]);

  const links = [
    { to: '/site', label: t.nav.home, exact: true },
    { to: '/site/platforma', label: t.nav.platforma },
    { to: '/site/solutii', label: t.nav.solutii },
    
    { to: '/site/industrii', label: t.nav.industrii },
    { to: '/site/despre', label: t.nav.despre },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-300',
        scrolled
          ? 'backdrop-blur-xl bg-background/70 border-b border-border/60 shadow-sm'
          : 'bg-transparent'
      )}
    >
      <div className="container mx-auto px-4 lg:px-6 h-24 flex items-center justify-between">
        <Link to="/site" className="flex items-center gap-3 group">
          <div className="relative h-[3.3rem] w-[3.3rem] rounded-xl bg-gradient-to-br from-primary via-primary/80 to-primary/40 grid place-items-center shadow-lg shadow-primary/20">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
            <div className="absolute inset-0 rounded-xl bg-primary/30 blur-xl -z-10 group-hover:bg-primary/50 transition" />
          </div>
          <div className="leading-none">
            <div className="font-black text-3xl tracking-tight">AETHERIS</div>
            <div className="text-[16px] uppercase tracking-[0.18em] text-muted-foreground">Platform OS</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.exact}
              className={({ isActive }) =>
                cn(
                  'px-3 py-2 text-base font-medium rounded-lg transition-colors',
                  isActive
                    ? 'text-foreground bg-foreground/5'
                    : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setLang(lang === 'ro' ? 'en' : 'ro')}
            className="hidden sm:flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors uppercase"
            aria-label="Toggle language"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang}
          </button>
          <button
            onClick={toggleTheme}
            className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <a
            href="https://aether-is.com/auth"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex h-9 items-center px-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t.nav.login}
          </a>
          <Button asChild size="sm" className="hidden sm:inline-flex shadow-lg shadow-primary/20">
            <Link to="/site/contact" className="gap-1.5">
              {t.nav.demo}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-9 w-9 grid place-items-center rounded-lg text-foreground hover:bg-foreground/5"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                className={({ isActive }) =>
                  cn(
                    'px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-foreground/5'
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-border my-2" />
            <button
              onClick={() => setLang(lang === 'ro' ? 'en' : 'ro')}
              className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-foreground/5 flex items-center gap-2"
            >
              <Globe className="h-4 w-4" /> {lang === 'ro' ? 'English' : 'Română'}
            </button>
            <a href="https://aether-is.com/auth" target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-foreground/5">
              {t.nav.login}
            </a>
            <Button asChild className="mt-1">
              <Link to="/site/contact">{t.nav.demo}</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter: React.FC = () => {
  const { t } = useSiteI18n();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-border/60 bg-card/40 backdrop-blur-md">
      <div className="container mx-auto px-4 lg:px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/40 grid place-items-center">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-black tracking-tight">AETHERIS</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-sm">{t.footer.tagline}</p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{t.footer.product}</div>
          <ul className="space-y-2 text-sm">
            <li><Link className="hover:text-primary text-foreground/80" to="/site/platforma">{t.footer.links.platforma}</Link></li>
            <li><Link className="hover:text-primary text-foreground/80" to="/site/solutii">{t.footer.links.solutii}</Link></li>
            
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{t.footer.company}</div>
          <ul className="space-y-2 text-sm">
            <li><Link className="hover:text-primary text-foreground/80" to="/site/despre">{t.footer.links.despre}</Link></li>
            <li><Link className="hover:text-primary text-foreground/80" to="/site/contact">{t.nav.contact}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{t.footer.legal}</div>
          <ul className="space-y-2 text-sm">
            <li><Link className="hover:text-primary text-foreground/80" to="/politica-confidentialitate">{t.footer.links.gdpr}</Link></li>
            <li><Link className="hover:text-primary text-foreground/80" to="/site">{t.footer.links.cookies}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border/60">
        <div className="container mx-auto px-4 lg:px-6 py-4 flex flex-col sm:flex-row items-center justify-between text-xs text-muted-foreground gap-2">
          <div>© {year} AETHERIS. {t.footer.rights}</div>
          <div className="flex items-center gap-3">
            <span>Made in EU</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
            <span>GDPR · NIS2 · DSA</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname, hash } = useLocation();
  React.useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (hash) return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, hash]);
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to main content
      </a>
      <SiteHeader />
      <main id="main-content" role="main" className="flex-1 pt-16">{children}</main>
      <SiteFooter />
    </div>
  );
};
